/**
 * Deep flattens an object.
 * The resulting object contains new properties that contain the nested value paths.
 *
 * Examples:
 * flatten({ a: { b: 1 }}) => { "a[b]": 1}
 * flatten({ a: [1, 2] }) => { "a[0]": 1, "a[1]": 2 }
 *
 * @param data Any object
 * @returns The flattened object
 */
export const flatten = (
  data: object,
  prefix = "",
): Record<string, string | number | boolean> => {
  let result: ReturnType<typeof flatten> = {};

  if (typeof data !== "object") {
    result[prefix] = data;
    return result;
  }

  for (const [key, value] of Object.entries(data)) {
    const fullKey = prefix ? `${prefix}[${key}]` : key;

    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        result = { ...result, ...flatten(item, `${fullKey}[${i}]`) };
      });
    } else if (typeof value === "object") {
      result = { ...result, ...flatten(value, fullKey) };
    } else {
      result[fullKey] = value;
    }
  }

  return result;
};

/**
 * Unflattens an object that was flattened by `flatten`.
 * Returns the object to its original state.
 *
 * @param data Flattened object
 * @returns The object to its original unflattened state
 */
export const unflatten = (
  data: Record<string, string | number | boolean>,
): Record<string, any> => {
  const result: ReturnType<typeof unflatten> = {};

  for (const key in data) {
    const keys = key
      .split("[")
      .map((part) => (part.endsWith("]") ? part.slice(0, -1) : part));
    let current = result;

    keys.forEach((keyPart, index) => {
      const isArray = /^\d+$/.test(keys[index + 1] || "");
      const currentKey: string | number = /^\d+$/.test(keyPart)
        ? Number(keyPart)
        : keyPart;

      if (!current[currentKey]) {
        current[currentKey] = isArray ? [] : {};
      }

      if (index === keys.length - 1) {
        current[currentKey] = data[key];
      } else {
        current = current[currentKey];
      }
    });
  }

  return result;
};
